import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Table, Typography, message, Divider, Space, Row, Col, Statistic } from 'antd';
import { DollarOutlined, TeamOutlined, ShopOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const PerformanceSalaryCalculator = ({ visible, onCancel, onCalculate, initialData = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [performanceItems, setPerformanceItems] = useState([]);

  useEffect(() => {
    if (visible) {
      loadBasicData();
      if (initialData) {
        form.setFieldsValue({
          department_id: initialData.department_id,
          store_id: initialData.store_id,
          employee_id: initialData.employee_id,
          period: initialData.period || new Date().toISOString().split('T')[0].substring(0, 7), // 默认当前月份
          performance_type: initialData.performance_type || 'department'
        });
      } else {
        form.resetFields();
        setCalculationResult(null);
      }
    }
  }, [visible, initialData, form]);

  const loadBasicData = async () => {
    try {
      // 这里应该从API获取部门、门店和员工数据
      // 暂时使用模拟数据
      setDepartments([
        { id: 1, name: '销售部' },
        { id: 2, name: '市场部' },
        { id: 3, name: '技术部' },
        { id: 4, name: '人力资源部' }
      ]);
      
      setStores([
        { id: 1, name: '北京旗舰店' },
        { id: 2, name: '上海分店' },
        { id: 3, name: '广州分店' },
        { id: 4, name: '深圳分店' }
      ]);
      
      setEmployees([
        { id: 1, name: '张三', employee_no: 'EMP001', department_id: 1 },
        { id: 2, name: '李四', employee_no: 'EMP002', department_id: 1 },
        { id: 3, name: '王五', employee_no: 'EMP003', department_id: 2 },
        { id: 4, name: '赵六', employee_no: 'EMP004', department_id: 3 }
      ]);
      
      setPerformanceItems([
        {
          id: 1,
          name: '销售业绩',
          type: 'department',
          target_amount: 1000000,
          unit: '元',
          gradient_rules: [
            { min_ratio: 0, max_ratio: 0.8, bonus_ratio: 0 },
            { min_ratio: 0.8, max_ratio: 1, bonus_ratio: 0.05 },
            { min_ratio: 1, max_ratio: 1.2, bonus_ratio: 0.1 },
            { min_ratio: 1.2, max_ratio: Infinity, bonus_ratio: 0.15 }
          ]
        },
        {
          id: 2,
          name: '门店业绩',
          type: 'store',
          target_amount: 500000,
          unit: '元',
          gradient_rules: [
            { min_ratio: 0, max_ratio: 0.8, bonus_ratio: 0 },
            { min_ratio: 0.8, max_ratio: 1, bonus_ratio: 0.08 },
            { min_ratio: 1, max_ratio: 1.2, bonus_ratio: 0.12 },
            { min_ratio: 1.2, max_ratio: Infinity, bonus_ratio: 0.2 }
          ]
        }
      ]);
    } catch (error) {
      console.error('加载基础数据失败:', error);
      message.error('加载基础数据失败');
    }
  };

  const handleCalculate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // 模拟计算过程
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 根据选择的业绩类型获取对应的项目
      const performanceItem = performanceItems.find(item => item.type === values.performance_type);
      
      if (!performanceItem) {
        message.error('未找到对应的业绩项目');
        setLoading(false);
        return;
      }
      
      // 计算业绩完成比例
      const actualAmount = parseFloat(values.actual_amount) || 0;
      const targetAmount = parseFloat(values.target_amount || performanceItem.target_amount) || 0;
      const completionRatio = targetAmount > 0 ? actualAmount / targetAmount : 0;
      
      // 根据梯度规则计算奖金比例
      let bonusRatio = 0;
      for (const rule of performanceItem.gradient_rules) {
        if (completionRatio >= rule.min_ratio && completionRatio < rule.max_ratio) {
          bonusRatio = rule.bonus_ratio;
          break;
        }
      }
      
      // 计算奖金金额
      const bonusAmount = actualAmount * bonusRatio;
      
      // 构建计算结果
      const result = {
        performance_type: values.performance_type,
        performance_item: performanceItem.name,
        period: values.period,
        target_amount: targetAmount,
        actual_amount: actualAmount,
        completion_ratio: completionRatio,
        bonus_ratio: bonusRatio,
        bonus_amount: bonusAmount,
        department_name: departments.find(d => d.id === values.department_id)?.name || '',
        store_name: stores.find(s => s.id === values.store_id)?.name || '',
        employee_name: employees.find(e => e.id === values.employee_id)?.name || '',
        calculation_time: new Date().toLocaleString()
      };
      
      setCalculationResult(result);
      
      if (onCalculate) {
        onCalculate(result);
      }
      
      message.success('业绩薪资计算成功');
    } catch (error) {
      console.error('计算失败:', error);
      message.error('计算失败，请检查输入数据');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setCalculationResult(null);
  };

  const columns = [
    {
      title: '梯度范围',
      dataIndex: 'range',
      key: 'range',
      render: (_, record) => {
        if (record.max_ratio === Infinity) {
          return `${(record.min_ratio * 100).toFixed(0)}% 及以上`;
        }
        return `${(record.min_ratio * 100).toFixed(0)}% - ${(record.max_ratio * 100).toFixed(0)}%`;
      }
    },
    {
      title: '奖金比例',
      dataIndex: 'bonus_ratio',
      key: 'bonus_ratio',
      render: (_, record) => `${(record.bonus_ratio * 100).toFixed(2)}%`
    }
  ];

  return (
    <Card
      title={
        <Space>
          <DollarOutlined />
          <span>业绩薪资计算器</span>
        </Space>
      }
      bordered={false}
      style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          performance_type: 'department',
          period: new Date().toISOString().split('T')[0].substring(0, 7) // 默认当前月份
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="performance_type"
              label="业绩类型"
              rules={[{ required: true, message: '请选择业绩类型' }]}
            >
              <Select placeholder="选择业绩类型">
                <Option value="department">
                  <Space>
                    <TeamOutlined />
                    <span>部门业绩</span>
                  </Space>
                </Option>
                <Option value="store">
                  <Space>
                    <ShopOutlined />
                    <span>门店业绩</span>
                  </Space>
                </Option>
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              name="period"
              label="计算周期"
              rules={[{ required: true, message: '请选择计算周期' }]}
            >
              <Input type="month" placeholder="选择月份" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="department_id"
              label="部门"
              rules={[{ required: true, message: '请选择部门' }]}
            >
              <Select placeholder="选择部门" showSearch optionFilterProp="children">
                {departments.map(dept => (
                  <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              name="store_id"
              label="门店"
            >
              <Select placeholder="选择门店" showSearch optionFilterProp="children">
                {stores.map(store => (
                  <Option key={store.id} value={store.id}>{store.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="employee_id"
              label="员工"
            >
              <Select placeholder="选择员工" showSearch optionFilterProp="children">
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>{emp.name} ({emp.employee_no})</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="target_amount"
              label="目标业绩"
              rules={[{ required: true, message: '请输入目标业绩' }]}
            >
              <Input
                prefix={<DollarOutlined />}
                placeholder="输入目标业绩金额"
                type="number"
                min={0}
                step={1000}
              />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              name="actual_amount"
              label="实际业绩"
              rules={[{ required: true, message: '请输入实际业绩' }]}
            >
              <Input
                prefix={<DollarOutlined />}
                placeholder="输入实际业绩金额"
                type="number"
                min={0}
                step={1000}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="备注"
        >
          <TextArea rows={3} placeholder="输入备注信息（可选）" />
        </Form.Item>

        <Divider />

        <Form.Item>
          <Row justify="center">
            <Space>
              <Button onClick={handleReset} style={{ width: 120 }}>重置</Button>
              <Button 
                type="primary" 
                icon={<DollarOutlined />} 
                onClick={handleCalculate} 
                loading={loading}
                style={{ width: 150 }}
              >
                开始计算
              </Button>
              <Button 
                type="default" 
                onClick={onCancel} 
                style={{ width: 100 }}
              >
                取消
              </Button>
            </Space>
          </Row>
        </Form.Item>
      </Form>

      {calculationResult && (
        <Card
          title={
            <Space>
              <DollarOutlined />
              <span>计算结果</span>
            </Space>
          }
          style={{ marginTop: 20 }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="目标业绩" 
                value={calculationResult.target_amount} 
                prefix="¥" 
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="实际业绩" 
                value={calculationResult.actual_amount} 
                prefix="¥" 
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="完成比例" 
                value={calculationResult.completion_ratio * 100} 
                suffix="%" 
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="奖金金额" 
                value={calculationResult.bonus_amount} 
                prefix="¥" 
                precision={2}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          </Row>

          <Divider style={{ margin: '20px 0' }} />

          <Title level={5}>计算详情</Title>
          <Table 
            dataSource={calculationResult.performance_item === '销售业绩' ? performanceItems[0]?.gradient_rules : performanceItems[1]?.gradient_rules}
            columns={columns}
            pagination={false}
            size="small"
            rowKey={(record, index) => index}
          />

          <Divider style={{ margin: '20px 0' }} />

          <Row gutter={16}>
            <Col span={12}>
              <Text strong>业绩类型：</Text>
              <Text>{calculationResult.performance_type === 'department' ? '部门业绩' : '门店业绩'}</Text>
            </Col>
            <Col span={12}>
              <Text strong>计算周期：</Text>
              <Text>{calculationResult.period}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text strong>部门：</Text>
              <Text>{calculationResult.department_name}</Text>
            </Col>
            <Col span={12}>
              <Text strong>门店：</Text>
              <Text>{calculationResult.store_name || '无'}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text strong>员工：</Text>
              <Text>{calculationResult.employee_name || '全部员工'}</Text>
            </Col>
            <Col span={12}>
              <Text strong>计算时间：</Text>
              <Text>{calculationResult.calculation_time}</Text>
            </Col>
          </Row>

          <Divider style={{ margin: '20px 0' }} />

          <Row justify="center">
            <Button 
              type="primary" 
              icon={<DollarOutlined />}
              onClick={() => {
                if (onCalculate) {
                  onCalculate(calculationResult);
                }
                message.success('业绩薪资已应用到薪资系统');
              }}
            >
              应用到薪资系统
            </Button>
          </Row>
        </Card>
      )}
    </Card>
  );
};

export default PerformanceSalaryCalculator;